import * as vp from 'vopay';
import { FunctionComponent, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DialogueHandler, SignersDialogueHandler } from './type';
import SignersContext from './SignersContext';

const { ConfirmationDialogue } = vp;

type SignersDialogueProps = {
  handleDialogue: DialogueHandler<SignersDialogueHandler>;
};

type FC = FunctionComponent<SignersDialogueProps>;

const SignersDialogue: FC = ({ handleDialogue }: SignersDialogueProps) => {
  const { t } = useTranslation('translations', { keyPrefix: 'SignersDialogue' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });

  const {
    showDeleteSignerConfirmAlert,
    showDeleteSignerErrorAlert,
    deleteSignerError,
    showDeleteSignerAlert,
    showEditSignerAlert,
    shouldShowMaximumSignerAlert,
  } = useContext(SignersContext);

  return useMemo(
    () => (
      <div>
        {/* Delete Signers Confirmation */}
        <ConfirmationDialogue
          isVisible={showDeleteSignerConfirmAlert}
          title={t('DeleteConfirm.title', { defaultValue: 'Are you sure you wish to delete the signer?' })}
          subTitle={t('DeleteConfirm.subTitle', { defaultValue: 'All related signer data will be deleted.' })}
          status="alert"
          confirm={{
            label: tGlobal('Buttons.cancel'),
            action: () => handleDialogue({ type: 'alertDelete', state: 'back' }),
          }}
          cancel={{
            label: t('DeleteConfirm.deleteSigner', { defaultValue: 'Delete Signer ' }),
            action: () => handleDialogue({ type: 'alertDelete', state: 'confirm' }),
          }}
        />

        {/* Delete Signer Error */}
        <ConfirmationDialogue
          isVisible={showDeleteSignerErrorAlert}
          title={t('DeleteError.title', { defaultValue: 'Error! The signer was not deleted.' })}
          status="error"
          subTitle={`${deleteSignerError}`}
          confirm={{
            label: tGlobal('Buttons.back'),
            action: () => handleDialogue({ type: 'delete', state: 'confirm' }),
          }}
        />

        {/* Maximun Signer Alert */}
        <ConfirmationDialogue
          isVisible={shouldShowMaximumSignerAlert}
          title={t('MaximumSigner.title', { defaultValue: 'Exceeding maximum amount of signers' })}
          status="alert"
          subTitle={t('MaximumSigner.subTitle', {
            defaultValue:
              'Each agreement may only have a maximum of 5 signers. If you wish to add an additional signor, please remove a signor first.',
          })}
          confirm={{
            label: tGlobal('Buttons.okay'),
            action: () => handleDialogue({ type: 'warning', state: 'confirm' }),
          }}
        />

        {/* Signer Edit Alert */}
        <ConfirmationDialogue
          isVisible={showEditSignerAlert}
          title={t('CannotEdit.title', { defaultValue: 'Cannot Edit Signer' })}
          status="alert"
          subTitle={t('CannotEdit.subTitle', { defaultValue: 'Signer is unable to be edited.' })}
          confirm={{
            label: tGlobal('Buttons.okay'),
            action: () => handleDialogue({ type: 'noEdit', state: 'confirm' }),
          }}
        />

        {/* Signer Delete Alert */}
        <ConfirmationDialogue
          isVisible={showDeleteSignerAlert}
          title={t('CannotDelete.title', { defaultValue: 'Cannot delete signer' })}
          status="alert"
          subTitle={t('CannotDelete.subTitle', { defaultValue: 'Signer is unable to be deleted.' })}
          confirm={{
            label: tGlobal('Buttons.okay'),
            action: () => handleDialogue({ type: 'noDelete', state: 'confirm' }),
          }}
        />
      </div>
    ),
    [
      showDeleteSignerConfirmAlert,
      showDeleteSignerErrorAlert,
      deleteSignerError,
      showEditSignerAlert,
      showDeleteSignerAlert,
      shouldShowMaximumSignerAlert,
      t,
      tGlobal,
    ],
  );
};

export default SignersDialogue;
